import React, { useRef, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { css } from '@emotion/core';
import { below } from 'utils/breakpoint';
import { useSecondaryPromoSelector } from 'client/game_photoContest/hooks/useReduxSelector';
import UgcCard from 'ugc/components/ugcCollections/UgcCard';
import { ImageShowTypeMapping } from 'editor/components/Gallery/SettingPreviews/defaultGameSetting';
import ButtonViewMore from 'ugc/components/ugcGallery/montage/ButtonViewMore';
import { gameTypes } from 'config/promoGameTypes';
import { IS_EDITOR_MODE } from 'config/envVariable';

export const tabsConfig = {
    columModeOrigin: 0,
    columModeWaterfall: 1
};

function SquareCardList({
    columMode = 1,
    config,
    data: collections = () => {},
    editor = false,
    buttonLink,
    disableLink = true,
    isInit,
    setIsInit,
    promoId
}) {
    const [absoluteStyle, setAbsoluteStyle] = useState(false);
    const { type: promoType } = useSecondaryPromoSelector(s => s.promo);
    const relatedWith = useSelector(s => s.relatedWith);
    const showInfo = config?.versionStyleSettings.isShowWorkInformation;
    const isWaterFall = config?.versionStyleSettings.imgShowType === ImageShowTypeMapping.WATERFALL;
    const isGallery = true;
    const { isShowButton, maskColor } = config?.buttonSettings;
    const squareRef = useRef(null);
    const igGame = (IS_EDITOR_MODE ? relatedWith?.gameType : promoType) === gameTypes.INSTAGRAM;

    useEffect(() => {
        const toCountScreenHeight = () => {
            const height = squareRef.current.offsetHeight;
            if (
                height === 32 ||
                height === 48 ||
                height === 64 ||
                height === 128 ||
                height === 240
            ) {
                setIsInit(false);
            }
            const buttonHeight = 168;
            const divTotalHeight = 880;

            if (height + buttonHeight > divTotalHeight) {
                setAbsoluteStyle(true);
            } else {
                setAbsoluteStyle(false);
            }
        };
        if (isInit) {
            toCountScreenHeight();
        }
        window.addEventListener('resize', toCountScreenHeight);
        return () => {
            window.removeEventListener('resize', toCountScreenHeight);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isInit]);

    function SquareContainer({ children }) {
        return <>{children}</>;
    }

    function RenderCards() {
        return collections(igGame, isGallery).map(
            (
                { avatar, memberId, media, ugcAuthorName, title, mediaLibraryTitle, nickname } = {},
                index
            ) => {
                return (
                    <div className={`fever-ugc-card-${index}`} key={index} css={getEntryItemStyle}>
                        <UgcCard
                            style={showInfo && cardBgStyle()}
                            textStyle={showInfo && cardTextStyle()}
                            avatar={avatar}
                            name={ugcAuthorName || nickname}
                            imgUrls={media}
                            title={title || mediaLibraryTitle}
                            serialNo={memberId}
                            isOriginRatio={columMode === tabsConfig.columModeOrigin}
                            showInfo={showInfo}
                            promoId={promoId}
                            promoType={promoType}
                            disableLink={disableLink}
                            isWaterFall={isWaterFall}
                            igGame={igGame}
                            isGallery={isGallery}
                            SetReady={setIsInit}
                            length={collections().length}
                            index={index}
                        />
                    </div>
                );
            }
        );
    }

    return (
        <>
            <div css={containerStyle(editor)}>
                <div css={getEntryListContainerStyle(isWaterFall)} ref={squareRef}>
                    <div id="fever-ugc-scroll-point" />
                    {/* ALAN TODO: props 用不到？ */}
                    <SquareContainer config={config.buttonSettings}>
                        {RenderCards()}
                    </SquareContainer>
                </div>
                {isShowButton ? (
                    <div
                        css={[
                            buttonContainerStyle(maskColor),
                            (absoluteStyle || editor) && buttonDivAbsolute
                        ]}
                    >
                        <a href={buttonLink} target="_blank" rel="noreferrer">
                            <ButtonViewMore config={config.buttonSettings} />
                        </a>
                    </div>
                ) : (
                    <div
                        css={[
                            buttonContainerStyle(maskColor),
                            (absoluteStyle || editor) && buttonDivAbsolute
                        ]}
                    ></div>
                )}
            </div>
        </>
    );
}

export default SquareCardList;

const containerStyle = editor => css`
    width: 100%;
    padding: 8px;
    ${editor && `flex-grow: 1;`}

    ${below('xsm')} {
        padding: 8px 0;
    }
`;

const cardBgStyle = () => `
    background:#fff;
`;
const cardTextStyle = () => `
    color:#333;
`;

const getEntryListContainerStyle = isWaterFall => css`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    width: 100%;
    ${isWaterFall
        ? `display: flex;
              align-items: center;
              justify-content: center;
              padding: 8px 0;`
        : 'justify-content: center;'};
`;

const getEntryItemStyle = css`
    max-width: 317px;
    min-width: 200px;
    width: 25%;
    flex: 0 0 calc(25% - 8px);
    margin-bottom: 16px;
    margin-right: 8px;
    background: #ffffff;
    box-shadow: 0px 1px 3px rgb(0 0 0 / 20%), 0px 2px 2px rgb(0 0 0 / 12%),
        0px 0px 2px rgb(0 0 0 / 14%);
    border-radius: 16px;
    ${below('xsm')} {
        min-width: 183px;
        &:nth-of-type(even) {
            margin-right: 8px;
        }
        &:nth-of-type(odd) {
            margin-right: 0px;
        }
    }
`;

const buttonContainerStyle = maskColor => css`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 168px;
    z-index: 2;
    background: linear-gradient(0deg, ${maskColor} 28.27%, rgba(250, 255, 0, 0) 94.35%);
`;

const buttonDivAbsolute = css`
    position: absolute;
    left: 0;
    right: 0;
    bottom: -8px;
    margin: auto;
`;
